import { useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { sdkQueryClient } from '../queryClient';
import type { Survey, SurveySession } from '../fetchSurvey/types/survey';
import type { SurveyAnswers, SubmitInput, SubmitRequestBody } from './types';
import { logResolvedInputs, logRequestBody, logExecuteSubmitError } from './logger/resolutionLogger';
import { toSdkLoggableError } from '../types/shared';

// Re-export all type definitions (both parameters and final mapped results)
export * from './types';
import {
  resolveSubmitInput,
  buildSubmitRequestBody,
  buildExternalSubmitUrl,
  submitSurveyResponse,
} from './utils';

/**
 * A custom React hook that manages submitting user answers to the remote survey response service.
 * Tracks submission data, loading states, and error responses using TanStack Query useMutation.
 *
 * @param session Active survey session details containing target instanceId and language.
 * @param survey Fully mapped survey structure containing questions.
 * @returns Object holding submit execution, result data, loading state, and error.
 */
export function useSubmitResponse(session?: SurveySession | null, survey?: Survey | null) {
  const mutation = useMutation(
    {
      mutationFn: ({ url, requestBody }: { url: string; requestBody: SubmitRequestBody }) =>
        submitSurveyResponse({ url, requestBody }),
    },
    sdkQueryClient
  );

  const submit = useCallback(
    async (input: SurveyAnswers | SubmitInput) => {
      try {
        const resolved = resolveSubmitInput(input, session, survey);
        logResolvedInputs(resolved);

        const surveyLanguage = resolved.surveyLanguage ?? 'en';
        const requestBody = buildSubmitRequestBody(resolved.answers, resolved.questions ?? []);
        logRequestBody(requestBody);

        const url = buildExternalSubmitUrl(resolved.instanceId, surveyLanguage);

        return await mutation.mutateAsync({ url, requestBody });
      } catch (err) {
        logExecuteSubmitError(toSdkLoggableError(err));
        throw err;
      }
    },
    [session, survey, mutation]
  );

  return {
    submit,
    data: mutation.data ?? null,
    loading: mutation.isPending,
    error: mutation.error,
  };
}
